import type { PickSubject } from '../../store/types'

// Renderer for SubjectKindPick. Polymorphic across pick_kind —
// resource_binding, confirm_destructive, paint_color, etc. all
// render through here. Server-side handler keys off pick_kind for
// the side-effect-on-allow; iOS/webview don't care.
//
// State (selectedOptionID, freeText) is owned by the parent card so
// the Allow button can gate on selection. This body just renders.
export function PickBody({
  subject,
  selectedOptionID,
  freeText,
  onSelectOption,
  onChangeFreeText,
}: {
  subject: PickSubject
  selectedOptionID: string | null
  freeText: string
  onSelectOption: (id: string) => void
  onChangeFreeText: (s: string) => void
}) {
  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ marginTop: 0 }}>{subject.prompt}</p>
      {subject.prompt_detail && (
        <p style={{ opacity: 0.65, fontSize: 12, marginTop: 0 }}>
          {subject.prompt_detail}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {subject.options.length === 0 && !subject.allow_free_text && (
          <div
            style={{
              padding: '12px',
              background: '#fff7e6',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            No options provided and free text isn't allowed. Deny is
            your only path.
          </div>
        )}
        {subject.options.map((opt) => {
          const selected = selectedOptionID === opt.id
          const intent = opt.intent ?? 'default'
          const style = optionStyle(intent, selected)
          return (
            <button
              key={opt.id}
              onClick={() => onSelectOption(opt.id)}
              style={style}
            >
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{opt.label}</span>
                {opt.badge && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 400,
                      opacity: 0.65,
                      padding: '1px 6px',
                      border: '1px solid currentColor',
                      borderRadius: 999,
                    }}
                  >
                    {opt.badge}
                  </span>
                )}
              </div>
              {opt.description && (
                <div style={{ opacity: 0.7, fontSize: 12, marginTop: 2 }}>
                  {opt.description}
                </div>
              )}
            </button>
          )
        })}
        {subject.allow_free_text && (
          <input
            type="text"
            placeholder="Or type something else…"
            value={freeText}
            onChange={(e) => onChangeFreeText(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: 4,
              border: '1px solid #ddd',
              fontSize: 16,
            }}
          />
        )}
      </div>
    </div>
  )
}

// Token-based intent → styling map. Client owns the visual treatment;
// server only names roles. Selected option gets a stronger border +
// tinted background; unselected keeps a neutral border with the
// intent's accent color hinted via the label area.
function optionStyle(
  intent: 'default' | 'primary' | 'destructive' | 'warning' | string,
  selected: boolean,
): React.CSSProperties {
  const palette: Record<string, { accent: string; bg: string }> = {
    default: { accent: '#0a84ff', bg: '#f0f8ff' },
    primary: { accent: '#0a84ff', bg: '#f0f8ff' },
    destructive: { accent: '#d33', bg: '#fff0f0' },
    warning: { accent: '#c80', bg: '#fff7e0' },
  }
  const tokens = palette[intent] ?? palette.default
  return {
    textAlign: 'left',
    padding: '10px 12px',
    borderRadius: 4,
    border: selected
      ? `2px solid ${tokens.accent}`
      : intent === 'destructive' || intent === 'warning'
        ? `1px solid ${tokens.accent}`
        : '1px solid #ddd',
    background: selected ? tokens.bg : 'white',
    color: intent === 'destructive' && !selected ? tokens.accent : 'inherit',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  }
}

// Helper for callers that want to read meta keys without type-juggling.
// Returns undefined when missing or wrong type.
export function pickMetaString(subject: PickSubject, key: string): string | undefined {
  const v = subject.meta?.[key]
  return typeof v === 'string' ? v : undefined
}
